import { equal } from "@avvoka/shared"
import type { V } from "@component-utils/types"
import type { PlaceholderProps, SharedProps } from "./types"
import type { useTheme } from "../components/VSSelectBase.theme"
import type { ThemeProp } from "@component-library/themes"

export function findSelectOption <T> (value: NoInfer<T> | undefined | null, field: keyof V.Select.Item<T>, options: V.Select.Option<T>[]): V.Select.Item<T> | undefined {
  if (typeof value === 'undefined') return undefined

  return options.reduce<V.Select.Item<T> | undefined>((memo, option) => {
    if (memo) return memo

    if ('type' in option) {
      if (option.type === 'group') return findSelectOption(value, field, option.options)
    } else {
      if (equal(option[field], value)) return option
    }
  }, undefined)
}

export function getSelectOptionsFromValues <T> (values: NoInfer<T>[], options: V.Select.Option<T>[]): V.Select.Item<T>[] {
  return options.reduce<V.Select.Item<T>[]>((memo, option) => {
    if ('value' in option) {
      if (values.includes(option.value)) memo.push(option)
    } else if (option.type === 'group') {
      memo.push(...getSelectOptionsFromValues(values, option.options))
    }

    return memo
  }, [])
}

type ExtractReturnType<T, K extends keyof T | 'self' | ((item: T) => unknown)> =
  K extends 'self' ? T :
    K extends (item: T) => infer R ? R :
      K extends keyof T ? T[K] : never

// Overload 1: when valueKey is 'self'
export function toSelectOptions<T, L extends keyof T | ((item: T) => unknown)>(
  items: T[],
  valueKey: 'self',
  labelKey: L
): Array<{ value: T, label: ExtractReturnType<T, L> }>

// Overload 2: when valueKey is a property key or function
export function toSelectOptions<
T,
  V extends keyof T | ((item: T) => unknown),
  L extends keyof T | ((item: T) => unknown) = V
  >(
    items: T[],
  valueKey: V,
  labelKey?: L
): Array<{ value: ExtractReturnType<T, V>, label: ExtractReturnType<T, L> }>

// Overload 3: catch-all implementation
export function toSelectOptions<T>(
  items: T[],
  valueKey: keyof T | 'self' | ((item: T) => unknown),
  labelKey: keyof T | ((item: T) => unknown) = valueKey as keyof T | ((item: T) => unknown)
): Array<{ value: unknown, label: unknown }> {
  const getProperty = (item: T, key: keyof T | 'self' | ((item: T) => unknown)) => {
    if (key === 'self') {
      return item
    }
    
    if (typeof key === 'function') {
      return key(item)
    }
    
    return item[key]
  }

  return items.map((item) => ({
    value: getProperty(item, valueKey),
    label: getProperty(item, labelKey)
  }))
}

export type VSSelectBaseProps<T> = SharedProps & PlaceholderProps & {
  /**
   * Array of selectable options
   */
  options: V.Select.Option<T>[]
  /**
   * Allow users to search through options
   */
  search?: boolean | {
    /**
     * Search using fuzzy search
     */
    fuzzy?: boolean
    /**
     * Search by all specified properties
     */
    all?: boolean
    /**
     * Text to display when user is searching through options
     */
    placeholder?: string
    /**
     * Allow options to be sorted
     */
    sortable?: boolean
  }
  open?: boolean | ((value: string) => T)
  /**
   * Allow users to clear selection (value is set to undefined)
   */
  allowEmpty?: boolean
  /**
   * Is field required (modifies behavior of isValid)
   */
  required?: boolean
  /**
   * Theme
   */
  theme?: ThemeProp<typeof useTheme>
  /**
   * Action buttons
   */
  actions?: V.Select.Action[]
}
